// Relatório Executivo em PDF do Analytics Comercial — jsPDF + autoTable, seguindo o mesmo
// padrão client-side de src/lib/lemitDossiePdf.ts (paleta de marca, sem depender de serviço
// externo). Helpers redeclarados aqui em vez de extraídos do arquivo do Lemit — evita
// tocar numa feature já em produção por um ganho marginal de DRY.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AnalyticsExportInput } from './analyticsComercialExport';

type RGB = [number, number, number];
const DARK: RGB = [33, 41, 19];
const OLIVE: RGB = [95, 108, 55];
const AMBER: RGB = [246, 169, 55];

const M = 14;
const PAGE_H = 297;

export function exportAnalyticsPdf(input: AnalyticsExportInput, insights: string[]): void {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 40;

    // Capa
    doc.setFillColor(...DARK);
    doc.rect(0, 0, pageW, 32, 'F');
    doc.setFillColor(...OLIVE);
    doc.rect(0, 30.6, pageW * 0.72, 1.4, 'F');
    doc.setFillColor(...AMBER);
    doc.rect(pageW * 0.72, 30.6, pageW * 0.28, 1.4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Dashboard Comercial Executivo', M, 16);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${input.periodoLabel} · Vigor Energy`, M, 24);

    doc.setTextColor(...DARK);
    if (input.filtrosLabel) {
        doc.setFontSize(9);
        doc.text(`Filtros ativos: ${input.filtrosLabel}`, M, y);
        y += 8;
    }

    const ensureSpace = (needed: number) => {
        if (y + needed > PAGE_H - 16) { doc.addPage(); y = 20; }
    };

    const section = (title: string, head: string[], body: (string | number)[][]) => {
        ensureSpace(20);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...OLIVE);
        doc.text(title, M, y);
        autoTable(doc, {
            startY: y + 3,
            head: [head],
            body,
            theme: 'grid',
            headStyles: { fillColor: OLIVE, textColor: 255, fontSize: 8.5 },
            styles: { fontSize: 8.5, textColor: DARK },
            alternateRowStyles: { fillColor: [246, 248, 240] },
            margin: { left: M, right: M },
        });
        y = (doc as any).lastAutoTable.finalY + 10;
        doc.setTextColor(...DARK);
    };

    section('Visão Geral', ['Indicador', 'Valor'], input.kpis.map(([k, v]) => [k, String(v)]));

    if (input.meta) {
        section('Meta x Realizado — ' + input.meta.mesLabel, ['Indicador', 'Valor'], [
            ['Meta (kWh)', input.meta.meta.toFixed(2)],
            ['Realizado (kWh)', input.meta.realizado.toFixed(2)],
            ['% da meta', `${input.meta.pct.toFixed(1)}%`],
            ['Gap (kWh)', input.meta.gap.toFixed(2)],
        ]);
    }

    section('Funil Comercial', ['Etapa', 'Leads', 'kWh'], input.funil.map(f => [f.etapa, f.leads, f.kwh.toFixed(2)]));

    section('Painel de Status', ['Status', 'Quantidade', 'kWh', '% do funil'], input.statusPanel.map(s => [s.status, s.qtd, s.kwh.toFixed(2), `${s.pct.toFixed(1)}%`]));

    if (input.canal.length > 0) {
        section('Performance por Canal', ['Canal', 'Leads', 'Aprovados', 'kWh aprovado', 'kWh assinado', 'Conversão'],
            input.canal.map(c => [c.canal, c.leads, c.aprovados, c.kwhAprovado.toFixed(2), c.kwhAssinado.toFixed(2), `${c.conversao.toFixed(1)}%`]));
    }

    section('Ranking Comercial', ['Consultor', 'Leads', 'Aprovados', 'kWh aprovado', 'Conversão'],
        input.ranking.map(r => [r.nome, r.leads, r.aprovados, r.kwhAprovado.toFixed(2), `${r.conversao.toFixed(1)}%`]));

    if (input.motivosPerda.length > 0) {
        section('Motivos de Perda', ['Motivo', 'Quantidade'], input.motivosPerda.map(([m, q]) => [m, q]));
    }

    if (insights.length > 0) {
        ensureSpace(16 + insights.length * 6);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...OLIVE);
        doc.text('Pontos de Atenção e Oportunidades', M, y);
        y += 7;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...DARK);
        insights.forEach(text => {
            const lines = doc.splitTextToSize(`• ${text}`, pageW - M * 2);
            ensureSpace(lines.length * 5);
            doc.text(lines, M, y);
            y += lines.length * 5 + 2;
        });
    }

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(150, 150, 150);
        doc.text(`VigorHub · Dashboard Comercial Executivo · página ${i} de ${pageCount}`, M, PAGE_H - 8);
    }

    doc.save(`Analytics_Comercial_${new Date().toISOString().slice(0, 10)}.pdf`);
}
